<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { FORTUNE_WHEEL_PRIZE_ID, FORTUNE_WHEEL_PRIZE_TYPE, type FortuneWheelPrize } from '@/types/shared/fortune-wheel'
import { useConfig } from '@/composables/game/config'
import { formatNumberFullTruncate } from '@/helpers/utils'
import { useFortuneWheel } from '@/composables/game/fortune-wheel'
import { usePlayer } from '@/composables/game/player'
import UsdtIcon from '@/components/icons/UsdtIcon.vue'
import CoinIcon from '@/components/icons/CoinIcon.vue'
import { useHapticFeedback } from '@/composables/game/haptic-feedback'
import { useAnalytics } from '@/composables/analytics'
import { twMerge } from 'tailwind-merge'
import GoldenWheelBackground from '@/assets/images/golden-wheel-background.png'
import {
  FORTUNE_WHEEL_REWARD,
  fortuneWheelPrizes,
  fortuneWheelRewards,
  goldenWheelRewards,
} from '@/types/fortune-wheel'
import { useScreenSize } from '@/composables/helpers/screen-size'
import api from '@/services/api'
import webapp from '@/services/webapp'
import NumberFormatTooltip from '@/components/NumberFormatTooltip.vue'
import { useShop } from '@/composables/game/shop'

const props = withDefaults(defineProps<{
	isSpinning: boolean
	isGold?: boolean
	onlyVisual?: boolean
	initFinished?: boolean
	isAutoSpin?: boolean
}>(), {
  isGold: false,
  onlyVisual: false,
  initFinished: false,
  isAutoSpin: false,
})
const emit = defineEmits<{
	(e: 'stopped', prize: FortuneWheelPrize | null): void
}>()
const wheel = ref()
const square = ref<HTMLElement | null>(null)

const previousEndDegree = ref(0)

const rewards = ref<FORTUNE_WHEEL_REWARD[]>([])
const rewardsCount = computed(() => rewards.value.length)
const { spin: _spin, checkOnboardingState } = useFortuneWheel()
const { updatePlayer } = usePlayer()
const { config } = useConfig()
const { addHapticFeedback } = useHapticFeedback()
const { triggerEvent } = useAnalytics()
const { width, height } = useScreenSize()
const { isIWD, isSTP } = useShop()

const wheelStyle = computed(() => {
  return {
    '--_items': rewardsCount.value,
  }
})

const setStyle = ref<boolean>(false)

const squareStyle = computed(() => {
  if (!square.value || !setStyle.value || props.onlyVisual || !height.value) return {
    opacity: props.onlyVisual ? '1' : '0',
  }
  const squareHeight = square.value?.offsetHeight
  const maxWidth = width.value - 32
  if (squareHeight > maxWidth) {
    return {
      height: `${maxWidth}px`,
      width: `${maxWidth}px`,
      flex: 'unset',
      opacity: 1,
    }
  }
  return {
    height: `${squareHeight}px`,
    width: `${squareHeight}px`,
    flex: 'unset',
    opacity: 1,
  }
})

const currentPrize = ref<FortuneWheelPrize | null>(null)
const finishedRotation = ref<boolean>(false)

const stopRotation = ref<boolean>(false)
const isFirstRotationFinished = ref<boolean>(false)
const finalAngleToRotate = ref<number>(0)
const sliceAngle = computed(() => 360 / rewardsCount.value) // Angle for each slice
const finalRounds = computed(() => props.isAutoSpin ? 2 : 4)

const startingSpeed = computed(() => props.isAutoSpin ? 30 : 20)
const extraSpins = computed(() => props.isAutoSpin ? 6 : 8) // Number of extra spins before stopping
function getItemStyle(index: number) {
  const reward = rewards.value[index]

  // Mirroring the angle by subtracting from 360
  const mirroredAngle = 360 - (index * sliceAngle.value)
  return {
    '--_items': rewardsCount.value,
    '--_idx': index + 1, // +1 because CSS nth-of-type starts at 1
    'align-content': 'center',
    'aspect-ratio': `1 / calc(2 * tan(180deg / ${rewardsCount.value}))`,
    'background': getColorByRewardId(reward, index),
    'clip-path': 'polygon(0% 0%, 100% 50%, 0% 100%)',
    'font-size': '5cqi',
    'grid-area': '1 / -1',
    'padding-left': '1ch',
    'rotate': `calc(${mirroredAngle}deg + 90deg)`, // Adjust rotation to start at 12 o'clock
    'transform-origin': 'center right',
    'width': '50cqi',
  }
}

function getRewardFirstPrize(reward: FORTUNE_WHEEL_REWARD): FortuneWheelPrize {
  const prizeId = fortuneWheelPrizes[reward][0]
  if (props.isGold) {
    return config.value.game.fortuneWheel.goldenWheel.prizes.find((prize) => prize.id === prizeId) ?? config.value.game.fortuneWheel.goldenWheel.prizes[0]
  }
  return config.value.game.fortuneWheel.prizes.find((prize) => prize.id === prizeId) ?? config.value.game.fortuneWheel.prizes.find((prize) => prize.id === FORTUNE_WHEEL_PRIZE_ID.POINT1) ?? config.value.game.fortuneWheel.prizes[0] //default to POINT1
}

function getCoinsRewardClass(amount: string) {
  if (amount.length <= 2) {
    return 'mb-3'
  }
  if (amount.length === 3) {
    return 'mb-1'
  }
  if (amount.length === 4) {
    return ''
  }
  if (amount.length === 5) {
    return '-mb-1.5'
  }
  if (amount.length === 6) {
    return '-mb-3'
  }
  if (amount.length === 7) {
    return '-mb-[22px]'
  }
  if (amount.length === 8) {
    return 'max-[375px]-mb-4 -mb-5 max-[375px]:!text-[10px] !text-xs'
  }
  return ''
}

function getColorByRewardId(rewardId: string, index: number) {
  const colorMap = {
    [FORTUNE_WHEEL_REWARD.SPIN1]: '#59B25C',
    [FORTUNE_WHEEL_REWARD.SPIN_GOLDEN1]: 'linear-gradient(269deg, #F5C250 -31.87%, #FFFF9F 65.88%, #FFCD58 97.27%)',
    [FORTUNE_WHEEL_REWARD.CHEST]: index < 5 ? '#706B35' : '#706B35',
    [FORTUNE_WHEEL_REWARD.USDT100]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.POINT3]: '#8C853A',
    [FORTUNE_WHEEL_REWARD.USDT500]: '#706B35',
    [FORTUNE_WHEEL_REWARD.POINT4]: 'linear-gradient(269deg, #F5C250 -31.87%, #FFFF9F 65.88%, #FFCD58 97.27%)',
    [FORTUNE_WHEEL_REWARD.USDT5]: '#1A1E18',
    [FORTUNE_WHEEL_REWARD.POINT1]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.USDT10]: '#8C853A',
    [FORTUNE_WHEEL_REWARD.GOLD_FEATHER_10]: '#1A1E18',
    [FORTUNE_WHEEL_REWARD.POINT2]: '#A138B3',
    [FORTUNE_WHEEL_REWARD.USDT50]: '#E72E6D',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT25]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT50]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT100]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT500]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN5]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN10]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN25]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN50]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.TICKET]: '#706B35',
  }

  const IWDMap = {
    [FORTUNE_WHEEL_REWARD.SPIN1]: '#59B25C',
    [FORTUNE_WHEEL_REWARD.SPIN_GOLDEN1]: 'linear-gradient(269deg, #F5C250 -31.87%, #FFFF9F 65.88%, #FFCD58 97.27%)',
    [FORTUNE_WHEEL_REWARD.CHEST]: index < 5 ? '#706B35' : '#706B35',
    [FORTUNE_WHEEL_REWARD.TICKET]: '#706B35',
    [FORTUNE_WHEEL_REWARD.USDT100]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.POINT3]: '#8C853A',
    [FORTUNE_WHEEL_REWARD.USDT500]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.POINT4]: 'linear-gradient(269deg, #F5C250 -31.87%, #FFFF9F 65.88%, #FFCD58 97.27%)',
    [FORTUNE_WHEEL_REWARD.USDT5]: '#1A1E18',
    [FORTUNE_WHEEL_REWARD.POINT1]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.USDT10]: '#8C853A',
    [FORTUNE_WHEEL_REWARD.GOLD_FEATHER_10]: '#1A1E18',
    [FORTUNE_WHEEL_REWARD.POINT2]: '#FB4CF1',
    [FORTUNE_WHEEL_REWARD.USDT50]: '#E72E6D',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT25]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT50]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT100]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT500]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN5]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN10]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN25]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN50]: '#FFF246',
  }

  const STPMap = {
    [FORTUNE_WHEEL_REWARD.SPIN1]: '#59B25C',
    [FORTUNE_WHEEL_REWARD.SPIN_GOLDEN1]: 'linear-gradient(269deg, #F5C250 -31.87%, #FFFF9F 65.88%, #FFCD58 97.27%)',
    [FORTUNE_WHEEL_REWARD.CHEST]: index < 5 ? '#706B35' : '#706B35',
    [FORTUNE_WHEEL_REWARD.TICKET]: '#706B35',
    [FORTUNE_WHEEL_REWARD.USDT100]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.POINT3]: '#8C853A',
    [FORTUNE_WHEEL_REWARD.USDT500]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.POINT4]: 'linear-gradient(269deg, #F5C250 -31.87%, #FFFF9F 65.88%, #FFCD58 97.27%)',
    [FORTUNE_WHEEL_REWARD.USDT5]: '#1A1E18',
    [FORTUNE_WHEEL_REWARD.POINT1]: 'linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%)',
    [FORTUNE_WHEEL_REWARD.USDT10]: '#8C853A',
    [FORTUNE_WHEEL_REWARD.GOLD_FEATHER_10]: '#1A1E18',
    [FORTUNE_WHEEL_REWARD.POINT2]: '#FF7900',
    [FORTUNE_WHEEL_REWARD.USDT50]: '#E72E6D',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT25]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT50]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT100]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_USDT500]: '#E5B80B',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN5]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN10]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN25]: '#FFF246',
    [FORTUNE_WHEEL_REWARD.GOLDEN_SPIN50]: '#FFF246',
  }

  if (isIWD.value) {
    return IWDMap[rewardId as keyof typeof IWDMap] ?? '#FFFFFF'
  }
  if (isSTP.value) {
    return STPMap[rewardId as keyof typeof STPMap] ?? '#FFFFFF'
  }
  return colorMap[rewardId as keyof typeof colorMap] ?? '#FFFFFF' // Fallback color if the ID is not found
}

function rotateWheel({
  totalRounds = 4,
  targetAngle = 360,
  startingSpeed = 12,
  eventInterval = 45,
  minSpeed = 0.1,
  decelerationStartDistance = 0,
  onEvent = (angle: number) => console.log(`Event triggered at ${angle % 360} degrees`),
  onFinish,
}: {
	totalRounds: number, targetAngle: number,
	startingSpeed: number,
	eventInterval?: number,
	minSpeed?: number,
	decelerationStartDistance?: number,
	onEvent?: (angle: number) => void
	onFinish: () => void

}) {
  let animationFrameId = 0
  let currentAngle = 0
  let currentSpeed = startingSpeed
  let lastEventAngle = -eventInterval / 2

  const fullRotationDegrees = totalRounds * 360
  const finalTargetAngle = fullRotationDegrees + targetAngle

  // Function to check and trigger events only in the final segment
  function checkEvents(angle: number) {
    if (lastEventAngle + eventInterval < angle && currentSpeed <= 6) {
      lastEventAngle += eventInterval
      onEvent(lastEventAngle)
    } else if (currentSpeed > 6 && (lastEventAngle + 2 * eventInterval < angle)) {
      lastEventAngle += 2 * eventInterval
      onEvent(lastEventAngle)
    }
  }

  function calculateSpeed(remainingDistance: number) {
    const decelerationStart = decelerationStartDistance // When to start slowing down
    let speed

    if (remainingDistance <= decelerationStart) {
      const progress = remainingDistance / decelerationStart
      // Calculate speed proportional to remaining distance, with no abrupt drop
      speed = Math.min(startingSpeed, Math.max(minSpeed, minSpeed + (startingSpeed - minSpeed) * progress))
    } else {
      // Maintain high speed during the majority of the distance
      speed = startingSpeed
    }
    return speed

  }

  function animate() {
    const remainingDistance = finalTargetAngle - currentAngle

    if (remainingDistance <= 0 || (stopRotation.value && currentAngle % 360 <= 0.1 && currentAngle > 360)) {
      currentAngle = finalTargetAngle
      wheel.value.style.transform = `rotate(${currentAngle}deg)`
      cancelAnimationFrame(animationFrameId)
      stopRotation.value = false
      onFinish()
      return
    }

    // Calculate the new speed based on remaining distance
    currentSpeed = calculateSpeed(remainingDistance)

    // Update the current angle
    currentAngle += currentSpeed

    // Ensure the angle does not exceed the target angle
    if (currentAngle > finalTargetAngle) {
      currentAngle = finalTargetAngle
    }

    wheel.value.style.transform = `rotate(${currentAngle}deg)`

    // Check for event triggers
    checkEvents(currentAngle)

    // Continue animation
    animationFrameId = requestAnimationFrame(animate)
  }

  animate()
}

async function doSpin() {
  addHapticFeedback()
  triggerEvent({ event: 'spin_fortune_wheel' })
  stopRotation.value = false
  wheel.value.style.transition = '' // Clear any existing transition
  wheel.value.style.transform = '' // Clear any existing transform
  await new Promise(requestAnimationFrame) // Ensure reflow is applied

  const startDegree = previousEndDegree.value % 360 + 180 // Normalize start degree within 0-360

  // Step 1: Set up the transition with a large angle for simulating infinite spinning
  const initialSpinAngle = extraSpins.value * 360 + startDegree
  if (!props.isAutoSpin) {
    rotateWheel({
      totalRounds: extraSpins.value,
      targetAngle: 0,
      eventInterval: sliceAngle.value,
      startingSpeed: startingSpeed.value,
      onEvent: () => addHapticFeedback('light'),
      onFinish: () => isFirstRotationFinished.value = true,
    })
  } else {
    isFirstRotationFinished.value = true
  }

  // Simulate an API call to get the reward
  const reward = await getRewardFromAPI() // Replace with your actual API call
  // Step 2: Calculate the exact stop angle based on the reward
  const rewardIndexes = !reward ? [] : rewards.value.reduce((indexes: number[], r, index) => {
    if (fortuneWheelPrizes[r].includes(reward.id)) {
      indexes.push(index)
    }
    return indexes
  }, [])
  if (!rewardIndexes.length) {
    await webapp.showAlert('Your prize has been added but we failed to show it. Please refresh the app.')
    await api.reportError({
      code: 'prizeNotFound',
      description: `Prize returned from backend not found on the${props.isGold ? ' golden' : ''} wheel`,
      metadata: reward ?? {},
    })
    emit('stopped', null)
    return
  }
  const rewardIndex = rewardIndexes[Math.floor(Math.random() * rewardIndexes.length)]
  currentPrize.value = reward
  const targetAngle = rewardIndex * sliceAngle.value // Angle corresponding to the reward
  finalAngleToRotate.value = finalRounds.value * 360 + startDegree + targetAngle - (initialSpinAngle % 360 + 180) // Adjust within the same transition
  // Update the previous end degreec
  previousEndDegree.value = finalAngleToRotate.value % 360
  stopRotation.value = true
}

async function getRewardFromAPI() {
  const result = await _spin()
  if (result) {
    updatePlayer(result.playerStats)
    checkOnboardingState().then()
    return result.prize
  }
  return null
}

function setRewards() {
  rewards.value = props.isGold ? goldenWheelRewards : fortuneWheelRewards

}

watch(() => props.isGold, () => {
  setRewards()
}, { immediate: true })

watch([finalAngleToRotate, isFirstRotationFinished], () => {
  if (finalAngleToRotate.value && isFirstRotationFinished.value) {
    if (!props.isAutoSpin) {
      rotateWheel({
        totalRounds: finalRounds.value,
        targetAngle: (finalAngleToRotate.value % 360),
        startingSpeed: startingSpeed.value,
        eventInterval: sliceAngle.value,
        minSpeed: 0.1,
        decelerationStartDistance: (finalRounds.value / 2) * 360 + (2 * Math.random() * 100), // Distance to start decelerating
        onEvent: () => addHapticFeedback('light'),
        onFinish: () => finishedRotation.value = true,
      })
    } else {
      finishedRotation.value = true
    }

    finalAngleToRotate.value = 0
    isFirstRotationFinished.value = false
  }
})

watch([finishedRotation, currentPrize], () => {
  if (finishedRotation.value && currentPrize.value) {
    emit('stopped', currentPrize.value)
  }
})

watch(() => props.isSpinning, () => {
  if (props.isSpinning) {
    currentPrize.value = null
    finishedRotation.value = false
    console.log('is spinning')
    doSpin()
  }
})

watch([square, () => props.initFinished], () => {
  if (!square.value || !props.initFinished) return
  nextTick(() => {
    setStyle.value = true
  })
}, { immediate: true })

watch(() => props.isGold, () => {
  setStyle.value = false
  setTimeout(() => setStyle.value = true, 400)
})

watch([width, height], () => {
  nextTick(() => {
    setStyle.value = false
    setTimeout(() => {
      setStyle.value = true
    }, 100)
  })
})
</script>
<template>
  <div
    class="relative flex flex-1 flex-col items-center"
    :class="{'z-[92]': isAutoSpin}"
  >
    <Transition name="fade-fast">
      <img
        v-if="isGold && !onlyVisual && setStyle"
        :src="GoldenWheelBackground"
        class="absolute top-[calc(50%-40px)] left-1/2 -translate-x-1/2 -translate-y-1/2 h-[calc(2*100%)] w-auto max-w-none"
      >
    </Transition>
    <div
      ref="square"
      class="max-w-full relative flex-1 transition-[opacity] duration-150"
      :class="[!setStyle && !props.onlyVisual && 'invisible', props.onlyVisual && 'w-full h-full flex items-center justify-center']"
      :style="squareStyle"
    >
      <img
        class="absolute inset-0 scale-[1.18] origin-center -top-[5%]"
        src="@/assets/images/periodic/xmas-2025/holly.webp"
      >
      <div
        :class="twMerge('relative bg-[#FFCA45] rounded-full w-full max-[400px]:p-1.5 p-2 z-[10]', onlyVisual && 'only-visual', isSpinning && 'z-[92]')"
        style="box-shadow: 0 0 20.716px 0 rgba(31, 31, 31, 0.66) inset, 0 0 45.851px 0 #000;"
      >
        <div
          v-if="rewards.length"
          :class="twMerge('ui-wheel-of-fortune', isGold && 'gold', props.onlyVisual && '!max-w-none', isAutoSpin && 'infinite-rotate')"
          :style="wheelStyle"
        >
          <ul
            ref="wheel"
          >
            <li
              v-for="(reward, index) in rewards"
              :key="index"
              :class="twMerge('flex items-center relative', isGold && 'gold', (reward === FORTUNE_WHEEL_REWARD.SPIN_GOLDEN1 || reward === FORTUNE_WHEEL_REWARD.POINT4) && 'black')"
              :style="getItemStyle(index)"
            >
              <div class="inset-0 absolute flex items-center">
                <span
                  v-if="getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.USDT"
                  class="rotate-90 flex flex-col items-center max-[400px]:gap-0.5 gap-1 ml-2"
                >
                  <UsdtIcon
                    :class="twMerge('max-[400px]:size-5 max-[400px]:min-w-[20px] size-6 min-w-[24px]', onlyVisual && 'max-[400px]:size-[38px] max-[400px]:min-w-[38px] size-[42px] min-w-[42px]')"
                  />
                  <span
                    class="text-black"
                    :class="[onlyVisual ? '!text-[26px] !leading-[34px]' : '', getCoinsRewardClass(formatNumberFullTruncate((getRewardFirstPrize(reward).amount / 100).toString()))]"
                  >
                    {{ getRewardFirstPrize(reward).amount / 100 }}
                  </span>
                </span>
                <span
                  v-else-if="getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.SPIN_GOLDEN"
                  class="rotate-90 flex flex-col items-center gap-1 ml-2"
                >
                  <img
                    class="max-[400px]:size-5 max-[400px]:min-w-[20px] size-6 min-w-[24px] object-cover"
                    src="@/assets/images/golden-wheel.png"
                  >
                  <span>{{ getRewardFirstPrize(reward).amount }} {{ ' ' }} spin</span>
                </span>
                <span
                  v-else-if="getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.GOLD_FEATHER"
                  class="rotate-90 flex flex-col items-center gap-1 ml-2"
                >
                  <img
                    class="w-[34px] min-w-[34px] object-cover"
                    src="@/assets/images/feather.png"
                    style="transform: translateY(10px) rotate(32deg);"
                  >
                  <span :class="getCoinsRewardClass(formatNumberFullTruncate(getRewardFirstPrize(reward).amount))">
                    {{ getRewardFirstPrize(reward).amount }}
                  </span>
                </span>

                <NumberFormatTooltip
                  v-else-if="getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.POINTS"
                  :number="getRewardFirstPrize(reward).amount"
                  :formatting-type="'full'"
                >
                  <span

                    class="rotate-90 flex flex-col items-center ml-2"
                  >
                    <CoinIcon class="w-auto max-[400px]:h-5 h-[24px]" />

                    <span
                      :class="getCoinsRewardClass(formatNumberFullTruncate(getRewardFirstPrize(reward).amount))"
                    >
                      {{ formatNumberFullTruncate(getRewardFirstPrize(reward).amount) }}
                    </span>
                  </span>
                </NumberFormatTooltip>
                <span
                  v-else-if="getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.PRIZE_BOX_ITEM || getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.TICKET"
                  class="rotate-90 flex items-center justify-center relative w-[73px] -ml-2 min-[400px]:-ml-3"
                >
                  <img
                    v-if="getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.PRIZE_BOX_ITEM"
                    class="w-[26px] min-[401]:w-[32px] h-auto object-cover z-[1]"
                    src="@/assets/images/chest.png"
                  >
                  <img
                    v-else
                    class="w-[26px] min-[401]:w-[32px] h-auto object-cover z-[1]"
                    src="@/assets/images/emojis/emoji-ticket.png"
                  >
                  <img
                    v-if="isSTP"
                    src="@/assets/images/periodic/STP/STP-wheel-background.png"
                    class="w-[60px] min-[401]:w-[68px] h-auto object-cover absolute"
                  >
                  <img
                    v-else
                    src="@/assets/images/golden-wheel-background.png"
                    class="w-[60px] min-[401]:w-[68px] h-auto object-cover absolute"
                  >
                </span>
                <span
                  v-else-if="getRewardFirstPrize(reward).type === FORTUNE_WHEEL_PRIZE_TYPE.SPIN"
                  class="rotate-90 flex flex-col items-center gap-1 ml-4"
                >
                  <img
                    :class="twMerge('max-[400px]:size-5 max-[400px]:min-w-[20px] size-6 min-w-[24px]', onlyVisual && 'max-[400px]:size-[38px] max-[400px]:min-w-[38px] size-[42px] min-w-[42px]')"
                    src="@/assets/images/fortune-wheel-without-border.png"
                  >
                  <span
                    class="text-black"
                    :class="onlyVisual && '!text-[26px] !leading-[34px]'"
                  >{{ getRewardFirstPrize(reward).amount }}</span>
                </span>
              </div>
            </li>
          </ul>
        </div>
        <img
          src="@/assets/images/wheel-arrow-big.png"
          :class="twMerge('absolute object-contain bottom-0 translate-y-1/2 left-1/2 -translate-x-1/2 h-8 min-[380px]:h-[46px]', onlyVisual && 'h-[54px]')"
        >
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.ui-wheel-of-fortune {
	all: unset;
	container-type: inline-size;
	direction: ltr;
	display: grid;
	position: relative;
	//@media (min-height: 1px) {
	//  width: 220px;
	//}
	//@media (min-height: 680px) {
	//  width: 250px;
	//}
	//@media (min-height: 740px) {
	//  width: 305px;
	//}
	//
	//@media (min-height: 900px) {
	//  width: 350px;
	//}

	max-width: 100%;
	width: auto;
	max-height: 100%;
	aspect-ratio: 1 / 1;

	&.gold {
		@apply w-full;
	}

	&.only-visual {
		width: 100%;
		max-width: unset;
	}

	@apply flex items-center justify-center;

	& > * {
		position: absolute;
	}

	ul {
		all: unset;
		clip-path: inset(0 0 0 0 round 50%);
		display: grid;
		inset: 0;
		place-content: center start;
		width: 100%;
		height: 100%;
		border-radius: 50%; /* Ensures the wheel is circular */
		overflow: hidden; /* Clip the items to the circle */
		aspect-ratio: 1/1;

		li {
			span {
				@apply text-center text-white font-bold -tracking-[0.32px] max-[400px]:text-xs text-sm;
			}
		}

		li.gold {
			span {
				@apply text-black
			}
		}

		li.black {
			span {
				@apply text-black
			}
		}
	}
}

@keyframes rotating {
	0% {
		transform: rotate(0);
	}
	100% {
		transform: rotate(360deg);
	}
}

.infinite-rotate {
	animation: rotating 0.3s linear infinite;
}
</style>
